import { FunctionComponent } from 'react';
import { createRoot } from 'react-dom/client';
import { Resources, Translation } from '../../@types/i18next';
import { ConfirmExportModal } from '../../common/components/ConfirmExportModal';
import { UnlinkIntegrationModal } from '../../common/components/UnlinkIntegrationModal';
import { ProfileType } from '../../common/enums/ProfileType';
import { withModalProviders } from '../../common/higher-order-components/with-modal-providers';
import { MODAL_CONTAINER_ID } from '../../common/higher-order-components/with-shared-providers';
import { ExitJiraConfigModal } from '../../organization/integrations/components/ExitJiraConfigModal';
import { UserLevel } from '../../organization/users/enums/UserLevel';
import { UserRole } from '../../organization/users/enums/UserRole';
import { AddTestersToProjectFlow } from '../controllers/AddTestersToProjectFlow';
import { ChangePasswordFlow } from '../controllers/ChangePasswordFlow';
import { CreateExceptionFlow } from '../controllers/CreateExceptionFlow';
import { CreateProjectFlow } from '../controllers/CreateProjectFlow';
import { CreateScanFlow } from '../controllers/CreateScanFlow';
import { CreateSlotFlow } from '../controllers/CreateSlotFlow';
import { CreateUserFlow } from '../controllers/CreateUserFlow';
import { DeleteExceptionFlow } from '../controllers/DeleteExceptionFlow';
import { DeleteProfileFlow } from '../controllers/DeleteProfileFlow';
import { DeleteProjectFlow } from '../controllers/DeleteProjectFlow';
import { DeleteSlotFlow } from '../controllers/DeleteSlotFlow';
import { DeleteUserFlow } from '../controllers/DeleteUserFlow';
import { ResetApiKeyFlow } from '../controllers/ResetApiKeyFlow';
import { ResetPasswordFlow } from '../controllers/ResetPasswordFlow';
import { UpdateProfileFlow } from '../controllers/UpdateProfileFlow';
import { UpdateProjectFlow } from '../controllers/UpdateProjectFlow';
import { UpdateSlotFlow } from '../controllers/UpdateSlotFlow';
import { UpdateUserFlow } from '../controllers/UpdateUserFlow';
import { UploadTraceFlow } from '../controllers/UploadTraceFlow';
import { Project } from '../models/Project';
import { SlotType } from '../models/SlotType';
import { PrefillSlot } from '../models/valueObjects/PrefillSlot';

export class ModalManager {
  /**
   * Displays a modal to add testers to the project
   * @returns an object containing the following values
   * - `projectId`: the id of the updated project or `undefined` if the modal was closed
   */
  addTestersToProject(projectId: number): Promise<{ projectId: number | undefined }> {
    return this.promisify(AddTestersToProjectFlow, { projectId }, (projectId) => ({ projectId }));
  }

  /**
   * Displays a modal to create a new project
   * @returns an object containing the following values
   * - `dismissed`: true if the modal was closed before the end of the flow, false otherwise
   * - `projectId`: the id of the last created project
   * - `slotId`: the id of the last created slot in the project identified by `projectId`
   * - `reportId`: the id of the report created in the slot identified by `slotId`
   */
  createProject(
    slotTypes: SlotType[],
    hasUnlimitedReports: boolean
  ): Promise<{ dismissed: boolean; projectId: number | undefined; slotId: number | undefined; reportId: number | undefined; integrationsClicked?: boolean }> {
    return this.promisify(
      CreateProjectFlow,
      { slotTypes, hasUnlimitedReports },
      (dismissed: boolean, projectId: number | undefined, slotId: number | undefined, reportId: number | undefined, integrationsClicked?: boolean) => ({
        dismissed,
        projectId,
        slotId,
        reportId,
        integrationsClicked,
      })
    );
  }

  /**
   * Displays a modal to create a new slot
   * @returns an object containing the following values
   * - `dismissed`: true if the modal was closed before the end of the flow, false otherwise
   * - `slotId`: the id of the last created slot
   * - `reportId`: the id of the report created in the slot identified by `slotId`
   */
  createSlot(projectId: number, slotTypes: SlotType[]): Promise<{ dismissed: boolean; slotId: number | undefined; reportId: number | undefined }> {
    return this.promisify(CreateSlotFlow, { projectId, slotTypes }, (dismissed: boolean, slotId: number | undefined, reportId: number | undefined) => ({
      dismissed,
      slotId,
      reportId,
    }));
  }

  /**
   * Displays a modal to delete a project
   * @param projectId the id of the project to delete
   * @returns an object containing the following values
   * - `projectId`: the id of the deleted project or `undefined` if the modal was closed
   */
  deleteProject(projectId: number, projectName: string): Promise<{ projectId: number | undefined }> {
    return this.promisify(DeleteProjectFlow, { projectId, projectName }, (projectId: number | undefined) => ({ projectId }));
  }

  /**
   * Displays a modal to delete a slot
   * @param slotId the id of the slot to delete
   * @returns an object containing the following values
   * - `slotId`: the id of the deleted slot or `undefined` if the modal was closed
   */
  deleteSlot(slotId: number, slotName: string): Promise<{ slotId: number | undefined }> {
    return this.promisify(DeleteSlotFlow, { slotId, slotName }, (slotId: number | undefined) => ({ slotId }));
  }

  /**
   * Displays a modal to update the details of a project
   * @param project object of the project to update
   * @returns an object containing the following values
   * - `projectId`: the id of the updated project or `undefined` if the modal was closed
   */
  updateProject(project: Project, hasUnlimitedReports: boolean): Promise<{ projectId: number | undefined }> {
    return this.promisify(UpdateProjectFlow, { project, hasUnlimitedReports }, (projectId: number | undefined) => ({ projectId }));
  }

  /**
   * Displays a modal to update the details of a slot
   * @param slotId the id of the slot to update
   * @returns an object containing the following values
   * - `slotId`: the id of the updated slot or `undefined` if the modal was closed
   */
  updateSlot(slot: PrefillSlot): Promise<{ slotId: number | undefined }> {
    return this.promisify(UpdateSlotFlow, { slot }, (slotId: number | undefined) => ({ slotId }));
  }

  /**
   * Displays a modal to upload a trace in one of the slots of a project and create a report with it
   * @param projectId the id of the project
   * @param slotId if provided, the slot will be pre-selected as upload target
   * @returns an object containing the following values
   * - `dismissed`: true if the modal was closed before the end of the flow, false otherwise
   * - `slotId`:
   *    - if a slot was created, the id of the created slot (whether or not a trace was uploaded)
   *    - if a slot was selected and a trace uploaded, the id of the selected slot
   *    - `undefined` otherwise
   * - `reportId`: the id of the report created in the slot identified by `slotId`
   */
  createReport(
    projectId: number,
    slotTypes: SlotType[],
    slotId?: number
  ): Promise<{ dismissed: boolean; slotId: number | null; reportId: number | undefined }> {
    return this.promisify(UploadTraceFlow, { projectId, slotTypes, slotId }, (dismissed: boolean, slotId: number | null, reportId: number | undefined) => ({
      dismissed,
      slotId,
      reportId,
    }));
  }

  /**
   * Displays a modal to confirm exit of Jira Issue Export Configuration flow before link is established
   */
  exitJiraConfigurationFlow(): Promise<{ confirmExit: boolean }> {
    return this.promisify(ExitJiraConfigModal, {}, (confirmExit: boolean) => ({ confirmExit }));
  }

  /**
   * Displays a modal to confirm unlinking integration
   * @param title the title of the integration. Must be a translation *key*.
   * @param tip optional tip message to provide extra instructions. Must be a translation *key*.
   * @returns `confirmUnlink` true if unlink was clicked
   */
  unlinkIntegration(title: Translation<Resources>, tip: Translation<Resources> | null): Promise<{ confirmUnlink: boolean }> {
    return this.promisify(UnlinkIntegrationModal, { title, tip }, (confirmUnlink: boolean) => ({ confirmUnlink }));
  }

  /**
   * Displays a modal to change the password of the current user
   * @param username the current user name necessary to set up the form attributes
   * @returns `changed` true if the password was changed
   */
  changePassword(username: string): Promise<{ changed: boolean }> {
    return this.promisify(ChangePasswordFlow, { username }, (changed) => ({ changed }));
  }

  /**
   * Displays a modal to reset the password of a user
   * @param userId the id of the user
   * @param username the name of the user
   */
  resetUserPassword(userId: number, username: string): Promise<void> {
    return this.promisify(ResetPasswordFlow, { userId, username }, () => {});
  }

  /**
   * Displays a modal to create a user
   * @returns an object containing the following values
   * - `userId`: the ID of the created user or `undefined` if the modal was closed
   * - `assignTestersClicked`: `true` if the user clicked on the CTA to assign the new user to projects
   */
  createUser(): Promise<{ userId: number | undefined; assignTestersClicked: boolean }> {
    return this.promisify(CreateUserFlow, {}, (userId, assignTestersClicked) => ({ userId, assignTestersClicked }));
  }

  /**
   * Displays a modal to update a user
   * @param userId the id of the user to update
   * @param preferredName the current preferredName of the user to update
   * @param level the current level of the user
   * @param roles the current roles of the user
   * @param isCurrentUser whether the user to update is the current user
   * @returns an object containing the following values
   * - `userId`: the id of the updated user or `undefined` if the modal was closed
   */
  updateUser(
    userId: number,
    preferredName: string | null,
    level: UserLevel,
    roles: UserRole[],
    isCurrentUser: boolean
  ): Promise<{ userId: number | undefined }> {
    return this.promisify(
      UpdateUserFlow,
      { userId, currentPreferredName: preferredName, currentLevel: level, currentRoles: roles, isCurrentUser },
      (userId) => ({ userId })
    );
  }

  /**
   * Displays a modal to delete a user
   * @param userId the id of the user to delete
   * @param username the name of the user to delete
   * @returns an object containing the following values
   * - `userId`: the id of the deleted user or `undefined` if the modal was closed
   */
  deleteUser(userId: number, username: string): Promise<{ userId: number | undefined }> {
    return this.promisify(DeleteUserFlow, { userId, username }, (userId: number | undefined) => ({ userId }));
  }

  /**
   * Displays a modal to create a project exception
   * @param projectId the target project of the exception
   * @returns an object containing the following values
   * - `exceptionId`: the id of the exception or `undefined` if the modal was closed
   */
  createException(projectId: number): Promise<{ exceptionId: number | undefined }> {
    return this.promisify(CreateExceptionFlow, { projectId }, (exceptionId: number | undefined) => ({ exceptionId }));
  }

  /**
   * Displays a modal to delete a project exception
   * @param exceptionId the id of the exception to delete
   * @returns an object containing the following values
   * - `exceptionId`: the id of the deleted exception or `undefined` if the modal was closed
   */
  deleteException(exceptionId: number): Promise<{ exceptionId: number | undefined }> {
    return this.promisify(DeleteExceptionFlow, { exceptionId }, (exceptionId: number | undefined) => ({ exceptionId }));
  }

  /* Displays a modal to update a profile
   * @param profileId the id of the profile to update
   * @param profileType the type of the profile
   * @param currentName the current name of the profile
   * @returns an object containing the following values
   * - `profileId`: the id of the updated profile or `undefined` if the modal was closed
   */
  updateProfile(profileId: number, profileType: ProfileType, currentName: string): Promise<{ profileId: number | undefined }> {
    return this.promisify(UpdateProfileFlow, { profileId, profileType, currentName }, (profileId) => ({ profileId }));
  }

  /**
   * Displays a modal to delete a profile
   * @param profileId the id of the profile to delete
   * @param profileName the name of the profile to delete
   * @returns an object containing the following values
   * - `profileId`: the id of the deleted profile or `undefined` if the modal was closed
   */
  deleteProfile(profileId: number, profileName: string): Promise<{ profileId: number | undefined }> {
    return this.promisify(DeleteProfileFlow, { profileId, profileName }, (profileId: number | undefined) => ({ profileId }));
  }

  /**
   * Displays a modal to start a new scan
   * @returns an object containing the following values
   * - `scanId`: the id of the created scan
   */
  createScan(): Promise<{ scanId: number | undefined }> {
    return this.promisify(CreateScanFlow, {}, (scanId) => ({ scanId }));
  }

  /**
   * Displays a modal to reset the API Key of the current user.
   * Updates the key in `localStorage` on success.
   */
  resetApiKey(): Promise<void> {
    return this.promisify(ResetApiKeyFlow, {}, () => {});
  }

  /**
   * Displays a modal to confirm exporting a large number of instances
   */
  confirmExport(instancesCount: number): Promise<{ confirmExport: boolean }> {
    return this.promisify(ConfirmExportModal, { instancesCount }, (confirmExport: boolean) => ({ confirmExport }));
  }

  private promisify<TReturn, TProps extends {}>(
    Component: FunctionComponent<TProps>,
    props: Partial<TProps>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapper: (...args: any[]) => TReturn,
    closedFnName = 'onClosed'
  ): Promise<TReturn> {
    const container = document.getElementById(MODAL_CONTAINER_ID)!;
    const activeElement = document.activeElement as HTMLElement | null;

    const root = createRoot(container);
    return new Promise((resolve) => {
      const componentProps = {
        ...props,
        [closedFnName]: (...args: unknown[]) => {
          root.unmount();
          // Restore focus on the previously active element
          activeElement?.focus();
          resolve(mapper(...args));
        },
      } as TProps;
      const Wrapped = withModalProviders(Component);
      root.render(<Wrapped {...componentProps} />);
    });
  }
}
